import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRightIcon, SwapIcon } from "../../icons";
import GuideHint from "../GetStartedGuide/GuideHint";
// import { formatCryptoAmount } from "../../utils/format";
import { FormatBalance } from "../../../helper";
import { useExchangeRate } from "../../hooks/useExchangeRate";
import ErrorMessage from "../../../components/Common/ErrorMessage";
import NewCommonButton from "../common/NewCommonButton";
import { formatCryptoValue } from "../../utils/format";

const EnterAmountStep = ({
  activeTab = "Buy",
  selectedFiatAccount,
  selectedCryptoAccount,
  amount,
  onAmountChange,
  onAccountClick,
  onAssetClick,
  onCryptoAccountClick,
  onDestinationClick,
  inputMode = "fiat",
  setInputMode,
  onContinue,
  error, 
  setError,
  spotlightActive = false,
  guideHint = null,
}) => {
  const inputRef = useRef(null);
  const amountScrollRef = useRef(null);

  const isSell = activeTab === "Sell";
  const [isAmountFocused, setIsAmountFocused] = useState(false);

  // Sync default input mode on tab change
  useEffect(() => {
    setInputMode?.(isSell ? "crypto" : "fiat");
  }, [isSell, setInputMode]);

  const fiatCode = selectedFiatAccount?.currency_code;
  const cryptoCode = selectedCryptoAccount?.currency_code;
  const cryptoDecimal = selectedCryptoAccount?.decimals;

  // Use the custom hook
  const {
    exchangeRateData,
    isRateLoading,
    primaryUnit,
    conversionLabel,
    toggleInputMode,
    handleClear,
    handleMax,
  } = useExchangeRate({
    fiatCode,
    cryptoCode,
    isSell,
    amount,
    onAmountChange,
    inputMode,
    setInputMode,
    cryptoDecimal
  });

  const displayAmount = amount || "0";

  // Dynamic font sizing
  const amountFontSize = useMemo(() => {
    const len = displayAmount.length;
    if (len <= 5) return "text-4xl sm:text-[64px] sm:leading-[99px]";
    if (len <= 8) return "text-3xl sm:text-[50px] sm:leading-[75px]";
    if (len <= 11) return "text-2xl sm:text-[38px] sm:leading-[58px]";
    return "text-xl sm:text-[28px] sm:leading-[44px]";
  }, [displayAmount.length]);

  const caretHeight = useMemo(() => {
    const len = displayAmount.length;
    if (len <= 5) return "h-8 sm:h-[50px]";
    if (len <= 8) return "h-6 sm:h-[38px]";
    if (len <= 11) return "h-5 sm:h-[28px]";
    return "h-4 sm:h-[22px]";
  }, [displayAmount.length]);

  const handleAmountInput = (event) => {
    const next = event.target.value.replace(/[^0-9.]/g, "");
    const parts = next.split(".");
    if (parts.length > 2) return;
    onAmountChange?.(next);
  };

  useEffect(() => {
    const scrollContainer = amountScrollRef.current;
    if (!scrollContainer || !amount) return;

    window.requestAnimationFrame(() => {
      scrollContainer.scrollLeft = scrollContainer.scrollWidth;
    });
  }, [amount]);

  const activeAccountBalance = isSell
    ? selectedCryptoAccount?.balance
    : selectedFiatAccount?.balance;

  const amountSection = (
    <>
      <h3 className="text-base font-semibold leading-7 text-text-primary">
        Enter Amount
      </h3>

      <div className="flex flex-col gap-1">
        <div className="relative flex h-[72px] w-full min-w-0 items-center gap-1.5 sm:h-[84px] sm:gap-2">
          <div
            ref={amountScrollRef}
            className="pointer-events-none relative min-w-0 max-w-[calc(100%-4rem)] overflow-x-auto hide-scrollbar sm:max-w-none"
          >
            <div className="flex w-max items-center gap-1.5">
              <span
                className={`shrink-0 font-normal text-text-primary sm:font-medium transition-all duration-200 ${amountFontSize}`}
              >
                {displayAmount}
              </span>
              <span
                className={`w-[3px] shrink-0 bg-[#4ab933] transition-all duration-200 ${caretHeight} ${
                  isAmountFocused ? "animate-caret-blink" : "opacity-0"
                }`}
                aria-hidden={!isAmountFocused}
              />
            </div>
          </div>

          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={handleAmountInput}
            onFocus={() => setIsAmountFocused(true)}
            onBlur={() => setIsAmountFocused(false)}
            className="absolute inset-y-0 left-0 z-10 w-[calc(100%-4rem)] cursor-text appearance-none border-0 bg-transparent text-transparent shadow-none outline-none ring-0 caret-transparent focus:border-0 focus:outline-none focus:ring-0 sm:w-[calc(100%-5rem)]"
            aria-label="Enter amount"
            autoFocus={true}
          />

          <span className="pointer-events-none relative z-0 shrink-0 bg-background-surface pl-1 font-normal text-text-secondary max-sm:tracking-normal sm:bg-transparent sm:text-[60px] sm:font-medium sm:leading-[99px] sm:tracking-[-4px]">
            {primaryUnit}
          </span>
        </div>

        {/* Action Row: Switch, Max, and Clear */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleInputMode}
            className="flex w-fit items-center gap-1.5 rounded-md p-1 transition-opacity hover:opacity-80 active:scale-95"
            aria-label="Switch amount unit"
          >
            <span className="flex size-4 -rotate-90 items-center justify-center text-[#4ab933]">
              <SwapIcon />
            </span>
            <p className="text-xs font-semibold leading-[21px] text-[#4ab933] underline decoration-dotted underline-offset-2">
              ≈ {isRateLoading ? "Calculating..." : conversionLabel}
            </p>
          </button>

          <button
            type="button"
            onClick={() => handleMax(activeAccountBalance)}
            className="text-xs font-semibold leading-[21px] text-[#4ab933] transition-colors hover:text-[#8ccc7e] active:scale-95"
          >
            Max
          </button>

          {amount && amount !== "0" && (
            <>
              <span className="text-xs text-text-secondary">•</span>
              <button
                type="button"
                onClick={handleClear}
                className="text-xs font-medium leading-[21px] text-text-secondary transition-colors hover:text-red-400 active:scale-95"
              >
                Clear
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );

  const bottomSection = (
    <div className="relative flex flex-col">
      {isSell ? (
        <>
          <button
            type="button"
            onClick={onCryptoAccountClick}
            className="flex w-full items-center justify-between rounded-[12px] px-4 py-2.5 text-left custom-transition hover:bg-option-hover"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
                <img
                  src={selectedCryptoAccount?.logo}
                  alt={selectedCryptoAccount?.currency_code}
                  className="size-9 rounded-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold leading-[16.5px] text-text-primary">
                  Sell
                </p>
                <p className="text-xs font-normal leading-[19.5px] text-text-secondary">
                  {selectedCryptoAccount?.currency_code}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-3">
              <p className="text-sm font-semibold leading-[16.5px] text-text-secondary">
                {/* {FormatBalance(selectedCryptoAccount?.balance, true)}{" "} */}
                {formatCryptoValue(selectedCryptoAccount?.balance, selectedCryptoAccount?.decimals || 8)} {" "}
                {selectedCryptoAccount?.currency_code}
              </p>

              <ChevronRightIcon className="shrink-0 text-text-secondary" />
            </div>
          </button>

          <div
            className="absolute left-[34px] top-[49px] h-4 w-px bg-[#555]"
            aria-hidden
          />

          <button
            type="button"
            onClick={onDestinationClick}
            className="flex w-full items-center justify-between rounded-[12px] px-4 py-2.5 text-left custom-transition hover:bg-option-hover"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
                <img
                  src={selectedFiatAccount?.logo}
                  alt={selectedFiatAccount?.currency_code}
                  className="size-9 rounded-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold leading-[19.5px] text-text-primary">
                  Receive in
                </p>
                <p className="text-sm font-normal leading-[16.5px] text-text-secondary">
                  {selectedFiatAccount?.title} | {selectedFiatAccount?.account_number}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-3">
              <p className="text-sm font-semibold leading-[16.5px] text-text-secondary">
                {FormatBalance(selectedFiatAccount?.balance)}{" "}
                {selectedFiatAccount?.currency_code}
              </p>

              <ChevronRightIcon className="shrink-0 text-text-secondary" />
            </div>
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onAccountClick}
            className="flex w-full items-center justify-between rounded-[12px] px-4 py-2.5 text-left custom-transition hover:bg-option-hover"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
                <img
                  src={selectedFiatAccount?.logo}
                  alt={selectedFiatAccount?.currency_code}
                  className="size-9 rounded-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold leading-[19.5px] text-text-primary">
                  Pay With
                </p>
                <p className="text-sm font-normal leading-[16.5px] text-text-secondary">
                  {selectedFiatAccount?.title} | {selectedFiatAccount?.account_number}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <p className="text-sm font-semibold leading-[16.5px] text-text-secondary">
                {FormatBalance(selectedFiatAccount?.balance)}{" "}
                {selectedFiatAccount?.currency_code}
              </p>

              <ChevronRightIcon className="shrink-0 text-text-secondary" />
            </div>
          </button>

          <div
            className="absolute left-[34px] top-[49px] h-4 w-px bg-[#555]"
            aria-hidden
          />

          <button
            type="button"
            onClick={onAssetClick}
            className="flex w-full items-center justify-between rounded-[12px] px-4 py-2.5 text-left custom-transition hover:bg-option-hover"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
                <img
                  src={selectedCryptoAccount?.logo}
                  alt={selectedCryptoAccount?.currency_code}
                  className="size-9 rounded-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold leading-[16.5px] text-text-primary">
                  Buy
                </p>
                <p className="text-xs font-normal leading-[19.5px] text-text-secondary">
                  {selectedCryptoAccount?.currency_code}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <p className="text-sm font-semibold leading-[16.5px] text-text-secondary">
                {/* {FormatBalance(selectedCryptoAccount?.balance, true)}{" "} */}
                {formatCryptoValue(selectedCryptoAccount?.balance, selectedCryptoAccount?.decimals || 8)}{" "}
                {selectedCryptoAccount?.currency_code}
              </p>

              <ChevronRightIcon className="shrink-0 text-text-secondary" />
            </div>
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col justify-between px-4 pt-4 sm:h-full sm:px-6">
      <ErrorMessage message={error} onClose={() => setError("")} />
      <div
        className={`relative flex shrink-0 flex-col gap-4 ${
          spotlightActive
            ? "z-40 max-sm:-mx-4 max-sm:rounded-t-[12px] max-sm:bg-background-surface max-sm:px-4 max-sm:pb-4 max-sm:pt-4 sm:rounded-none sm:bg-transparent sm:p-0"
            : ""
        }`}
      >
        {amountSection}
        {spotlightActive && guideHint && (
          <div
            className={`pointer-events-none absolute inset-x-4 top-full z-50 mt-8 sm:hidden ${
              isAmountFocused ? "invisible" : ""
            }`}
          >
            <div className="pointer-events-auto mx-auto w-[80%] max-w-[250px]">
              <GuideHint
                title={guideHint.title}
                description={guideHint.description}
                primaryAction={guideHint.primaryAction}
                secondaryAction={guideHint.secondaryAction}
                onSkip={guideHint.onSkip}
                onDone={guideHint.onDone}
                onDepositCrypto={guideHint.onDepositCrypto}
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-auto flex shrink-0 flex-col gap-5 sm:gap-4 max-sm:pb-4">
        {bottomSection}
        <NewCommonButton
          className={"!h-12"}
          disabled={!amount || amount === "0" || !!error}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onContinue?.(inputMode, exchangeRateData)}
        >
          Continue
        </NewCommonButton>
      </div>
    </div>
  );
};

export default EnterAmountStep;